import {get, map, set, unset, isNil, isNumber} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';

import {Icon} from 'components/UIKit/Icon';

type ChildrenProps = {
    item: any;
    path: string;
    index: number;
    last?: boolean;
    onRemove?: (data: {index: number, item: any}) => void;
    renderActions: (data: {index: number, item: any}) => React.ReactNode;
}

type Props = {
    values: any;
    itemsPath: string;
    defaultValue?: any;
    tempItemsPathPrefix?: string;
    maxItems?: number;
    isValid?: (item: any) => boolean;
    onRemove: (data: {index: number, item: any}) => void;
    onAdd: (item: any) => void;
    children: (props: ChildrenProps) => React.ReactNode;
}
export type MultiInputProps = Props;
export type MultiInputChildren = (props: ChildrenProps) => React.ReactNode;

export const MultiInput: React.FC<Props> = ({
    values,
    itemsPath,
    defaultValue,
    tempItemsPathPrefix = '__',
    maxItems,
    isValid,
    onAdd,
    onRemove,
    children
}: Props) => {
    const renderItemActions = useCallback(({index, item}) => (
        <Icon name='delete' color='alert' button onClick={() => onRemove({index, item})} />
    ), [onRemove]);

    const [initialized, setInitialized] = useState(false);
    const items: any[] = useMemo(() => get(values, itemsPath) || [], [values, itemsPath]);
    const tempItemsPath = useMemo(() => `${tempItemsPathPrefix}${itemsPath}`, [itemsPath, tempItemsPathPrefix]);
    const getItemPath = useCallback((index: number) => `${itemsPath}[${index}]`, [itemsPath]);
    const lastItemPath = useMemo(() => `${tempItemsPath}[0]`, [tempItemsPath]);
    const getLastItem = useCallback(() => get(values, lastItemPath), [values, lastItemPath]);
    const isLastItemValid = useMemo(() => isValid ? isValid(getLastItem()) : true, [getLastItem, isValid]);
    const canAddItem = useMemo(() => !isNumber(maxItems) || items?.length < maxItems, [items?.length, maxItems]);

    const handleAdd = useCallback(() => {
        const lastItem = getLastItem();
        if (!isNil(defaultValue)) {
            set(values, lastItemPath, defaultValue);
        } else {
            unset(values, tempItemsPath);
        }
        onAdd(lastItem);
    }, [values, getLastItem, tempItemsPath, lastItemPath, defaultValue, onAdd]);

    useEffect(() => {
        if (!initialized) {
            if (!isNil(defaultValue) && isNil(getLastItem())) {
                set(values, lastItemPath, defaultValue);
            }
            setInitialized(true);
        }
    }, [initialized, defaultValue, values, lastItemPath, getLastItem]);

    useEffect(() => {
        if (initialized && !isNil(defaultValue) && isNil(getLastItem())) {
            setInitialized(false);
        }
    }, [initialized, defaultValue, getLastItem]);

    const renderLastItemActions = useCallback(() => (
        <Icon name='plus-circle' color='primary' size='md' disabled={!isLastItemValid} button onClick={handleAdd} />
    ), [isLastItemValid, handleAdd]);

    if (!initialized) {
        return null;
    }

    return (
        <Fragment>
            {map(items, (item, index) =>
                children({
                    item,
                    index,
                    path: getItemPath(index),
                    onRemove,
                    renderActions: renderItemActions
                }))}
            {canAddItem && children({
                item: getLastItem(),
                path: lastItemPath,
                index: items.length,
                last: true,
                renderActions: renderLastItemActions
            })}
        </Fragment>
    );
};
